import {
  Box,
  Flex,
  Icon,
  IconButton,
  Image as ChakraImage,
  Spinner,
  Text,
} from '@chakra-ui/react'
import {
  Download,
  File,
  FileSpreadsheet,
  FileText,
  Image,
  RotateCw,
  X
} from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../../api'
import { UploadedAsset } from '../../types/chatTypes'
import { Attachment, IMAGE_FILE_TYPES } from '../../utils/documentUtils'

// Function to get document icon and styling based on content type
function getDocumentDisplay(contentType: string, filename: string) {
  const lowerFilename = filename.toLowerCase();

  if (contentType.includes('pdf') || lowerFilename.endsWith('.pdf')) {
    return {
      icon: FileText,
      iconColor: 'red.500',
      borderColor: 'red.200',
      type: 'PDF'
    };
  }

  if (contentType.includes('sheet') || contentType.includes('excel') ||
      lowerFilename.endsWith('.xlsx') || lowerFilename.endsWith('.xls')) {
    return {
      icon: FileSpreadsheet,
      iconColor: 'green.500',
      borderColor: 'green.200',
      type: 'Spreadsheet'
    };
  }

  if (lowerFilename.endsWith('.csv')) {
    return {
      icon: FileSpreadsheet,
      iconColor: 'green.500',
      borderColor: 'green.200',
      type: 'CSV'
    };
  }

  if (contentType.includes('word') || contentType.includes('document') ||
      lowerFilename.endsWith('.docx') || lowerFilename.endsWith('.doc')) {
    return {
      icon: FileText,
      iconColor: 'blue.500',
      borderColor: 'blue.200',
      type: 'Document'
    };
  }

  if (contentType.includes('image') ||
      lowerFilename.endsWith('.jpg') || lowerFilename.endsWith('.jpeg') ||
      lowerFilename.endsWith('.png') || lowerFilename.endsWith('.webp')) {
    return {
      icon: Image,
      iconColor: 'purple.500',
      borderColor: 'purple.200',
      type: 'Image'
    };
  }

  // Default for unknown types
  return {
    icon: File,
    iconColor: 'gray.500',
    borderColor: 'gray.200',
    type: 'File'
  };
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export const MessageAttachment: React.FC<{ document: UploadedAsset }> = ({ document }) => {
  const documentDisplay = getDocumentDisplay(document.content_type, document.filename);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await apiClient.document.downloadDocument(document.id);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.filename;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // axiosClient interceptor already surfaces the error toast.
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box
      maxW="16rem"
      p={2}
      border="1px"
      borderColor={documentDisplay.borderColor}
      borderRadius="md"
      bg="app.bg"
      _hover={{
        borderColor: documentDisplay.iconColor,
      }}
      transition="all 0.2s"
    >
      <Flex alignItems="center" gap={2}>
        <Icon
          as={documentDisplay.icon}
          boxSize={4}
          color={documentDisplay.iconColor}
        />
        <Box flex="1" minW="0">
          <Text
            fontWeight="medium"
            color="app.text.primary"
            noOfLines={1}
            fontSize="sm"
            title={document.filename}
          >
            {document.filename}
          </Text>
          <Text
            fontSize="xs"
            color="app.text.muted"
          >
            {documentDisplay.type} • {formatFileSize(document.file_size)}
          </Text>
        </Box>
        <IconButton
          aria-label="Download document"
          icon={<Download size={14} />}
          size="xs"
          variant="ghost"
          onClick={handleDownload}
          isLoading={isDownloading}
          color={documentDisplay.iconColor}
          _hover={{
            bg: 'app.bg.hover',
          }}
        />
      </Flex>
    </Box>
  );
};

// A file being attached to the message that is still being written. It reads
// from the picked File, so it renders before the upload has answered.
export const InputAttachment: React.FC<{ attachment: Attachment; onRemove: () => void; onRetry: () => void }> = ({
  attachment,
  onRemove,
  onRetry,
}) => {
  const { t } = useTranslation();
  const { file, status } = attachment;
  const failure = attachment.status === 'error' ? attachment : undefined;
  const reason =
    failure && (failure.error === 'documentRejected' ? failure.message : String(t(`chat.${failure.error}`)));
  const documentDisplay = getDocumentDisplay(file.type, file.name);
  const isImage = IMAGE_FILE_TYPES.includes(file.type);
  const [previewUrl, setPreviewUrl] = useState<string>();

  useEffect(() => {
    if (!isImage) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);

  return (
    <Flex
      align="center"
      gap={2}
      maxW="16rem"
      p={2}
      border="1px"
      borderColor={failure ? 'red.300' : 'app.border'}
      borderRadius="md"
      bg="app.bg"
    >
      {previewUrl ? (
        <ChakraImage src={previewUrl} alt="" boxSize={8} objectFit="cover" borderRadius="sm" flexShrink={0} />
      ) : (
        <Icon as={documentDisplay.icon} boxSize={5} color={documentDisplay.iconColor} flexShrink={0} />
      )}
      <Box minW="0">
        <Text fontSize="sm" fontWeight="medium" color="app.text.primary" noOfLines={1} title={file.name}>
          {file.name}
        </Text>
        {failure ? (
          <Text fontSize="xs" color="red.500" noOfLines={1} title={reason}>
            {reason}
          </Text>
        ) : (
          <Flex align="center" gap={1} fontSize="xs" color="app.text.muted">
            {status === 'uploading' && <Spinner size="xs" />}
            <Text noOfLines={1}>{status === 'uploading' ? t('chat.uploadingFile') : formatFileSize(file.size)}</Text>
          </Flex>
        )}
      </Box>
      {failure?.error === 'documentUploadFailed' && (
        <IconButton
          aria-label={t('chat.retryAttachment', { name: file.name })}
          icon={<RotateCw size={14} />}
          size="xs"
          variant="ghost"
          flexShrink={0}
          onClick={onRetry}
        />
      )}
      <IconButton
        aria-label={t('chat.removeAttachment', { name: file.name })}
        icon={<X size={14} />}
        size="xs"
        variant="ghost"
        flexShrink={0}
        onClick={onRemove}
      />
    </Flex>
  );
};
